import React, { useContext, useEffect, useMemo, useState, useCallback } from 'react';
import styled from 'styled-components';

import { DataElementContext } from '../../../page-components/common/DataElementContext';
import { processComponentProps } from '../../../page-components/utils/processComponentProps';
import { useAppSelector, useAppDispatch } from '../../../store';
import { hidePopup, showPopup as showPopupAction } from '../../../store/slices/templatesConfig';
import { selectCurrentNotification } from '../../../store/slices/notifications';
import { 
  executeAction as executeNotificationAction,
  getActionLabel,
  getVisibleActions,
  getPrimaryActions as getPrimaryActionsHelper,
  getSecondaryActions as getSecondaryActionsHelper,
  type NotificationAction,
} from '../notification-manager/utils/notificationActionHelper';

export type NotificationTriggeredPopupProps = {
  children: any;
  styleText: string;
  className: string;
  properties?: {
    popupID?: string; // The ID displayModal will use to trigger this popup
    durationMs?: number; // Optional: kept for backward-compat but unused here
  };
};

const ModuleElementDiv = styled.div<{ $styleText: string }>((props) => props.$styleText);

/**
 * NotificationTriggeredPopup - A popup module that can be triggered by notification actions
 *
 * This module listens to templatesConfig.popupVisibility and shows/hides
 * the popup based on whether its popupID is set to visible.
 */
const NotificationTriggeredPopup = (componentProps: NotificationTriggeredPopupProps) => {
  let props = componentProps;

  const dataElementContext = useContext(DataElementContext);
  [props] = processComponentProps(props, dataElementContext);

  const dispatch = useAppDispatch();
  const popupID = props.properties?.popupID; // use configured popup id per instance
  const popupVisibility = useAppSelector((state) => state.templatesConfig.popupVisibility);
  const popupData = useAppSelector((state) => state.templatesConfig.popupData || {});
  const currentNotification = useAppSelector(selectCurrentNotification) as any;

  // Prefer live currentNotification; fallback to one captured at showPopup time
  const effectiveNotification = useMemo(() => {
    return currentNotification || (popupID ? popupData?.[popupID]?.notification : undefined);
  }, [currentNotification, popupID, popupData]);

  const [showPopup, setShowPopup] = useState(false);
  const [isOpen, setIsOpen] = useState(false);

  // Keep isOpen synchronized with showPopup (no unconditional open)
  useEffect(() => {
    setIsOpen(showPopup);
  }, [showPopup]);

  const effectivePopupID = useMemo(() => {
    if (popupID) return popupID;

    if ((dataElementContext as any)?.templateId === 'default-notification') {
      return 'default-notification';
    }

    return undefined as unknown as string; // no popup id => do not open
  }, [popupID, dataElementContext]);

  useEffect(() => {
    if (!effectivePopupID) return;

    if (popupVisibility[effectivePopupID] !== undefined) {
      const shouldShow = !!popupVisibility[effectivePopupID];
      setShowPopup(shouldShow);
      setIsOpen(shouldShow);
    } else {
      setShowPopup(false);
      setIsOpen(false);
    }
  }, [effectivePopupID, popupVisibility]);

  const closePopupHandler = useCallback(() => {
    setShowPopup(false);
    setIsOpen(false);
    if (effectivePopupID) {
      dispatch(hidePopup({ popupId: effectivePopupID }));
    }
  }, [dispatch, effectivePopupID]);

  // Expose action helpers (mirror NotificationManager)
  const executeAction = useCallback(async (action: NotificationAction) => {
    return await executeNotificationAction(action, () => {
      if (effectivePopupID) dispatch(hidePopup({ popupId: effectivePopupID }));
    }, { dispatch, currentNotification: effectiveNotification });
  }, [dispatch, effectivePopupID, effectiveNotification]);

  const getActionLabelHelper = useCallback((action: NotificationAction) => {
    return getActionLabel(action);
  }, []);

  const getVisibleActionsHelper = useCallback(() => {
    return effectiveNotification?.actions ? getVisibleActions(effectiveNotification.actions) : [];
  }, [effectiveNotification]);

  const getPrimaryActions = useCallback(() => {
    const visibleActions = getVisibleActionsHelper();
    return visibleActions.filter(a => a.type === 'primary');
  }, [getVisibleActionsHelper]);

  const getSecondaryActions = useCallback(() => {
    const visibleActions = getVisibleActionsHelper();
    return visibleActions.filter(a => a.type === 'secondary');
  }, [getVisibleActionsHelper]);

  // New: single primary/secondary and generic execute wrapper as requested
  const getNotificationPrimaryAction = useCallback((notification?: any) => {
    const actions = notification?.actions ? getVisibleActions(notification.actions) : getVisibleActionsHelper();
    const list = actions.filter(a => a.type === 'primary');
    return list.length ? list[0] : null;
  }, [getVisibleActionsHelper]);

  const getNotificationSecondaryAction = useCallback((notification?: any) => {
    const actions = notification?.actions ? getVisibleActions(notification.actions) : getVisibleActionsHelper();
    const list = actions.filter(a => a.type === 'secondary');
    return list.length ? list[0] : null;
  }, [getVisibleActionsHelper]);

  const executeNotificationActionWrapper = useCallback(async (notification: any, action: NotificationAction | undefined | null) => {
    if (!action) return;
    const ctxNotification = notification || effectiveNotification;
    await executeNotificationAction(action, () => {
      if (effectivePopupID) dispatch(hidePopup({ popupId: effectivePopupID }));
    }, { dispatch, currentNotification: ctxNotification });
  }, [dispatch, effectivePopupID, effectiveNotification]);

  const onToggleHandler = useCallback(() => {
    const newState = !isOpen;
    setIsOpen(newState);
    setShowPopup(newState);

    if (effectivePopupID) {
      if (newState) {
        dispatch(showPopupAction({ popupId: effectivePopupID }));
      } else {
        dispatch(hidePopup({ popupId: effectivePopupID }));
      }
    }
  }, [isOpen, effectivePopupID, dispatch]);

  const contextValue = useMemo(() => {
    // Prefer live currentNotification; fallback to one captured at showPopup time
    const effectiveCtxNotification = effectiveNotification;
    return {
      ...dataElementContext,
      showPopup,
      isOpen,
      closePopupHandler,
      popupID: effectivePopupID,
      onToggle: onToggleHandler,
      onToggleHandler: onToggleHandler,
      currentNotification: effectiveCtxNotification,
      executeAction,
      getActionLabel: getActionLabelHelper,
      getVisibleActions: getVisibleActionsHelper,
      getPrimaryActions,
      getSecondaryActions,
      getNotificationPrimaryAction,
      getNotificationSecondaryAction,
      executeNotificationActionWrapper,
    };
  }, [
    dataElementContext,
    showPopup,
    isOpen,
    closePopupHandler,
    effectivePopupID,
    onToggleHandler,
    effectiveNotification,
    executeAction,
    getActionLabelHelper,
    getVisibleActionsHelper,
    getPrimaryActions,
    getSecondaryActions,
    getNotificationPrimaryAction,
    getNotificationSecondaryAction,
    executeNotificationActionWrapper,
  ]);

  // Don't render if no popupID is configured
  if (!effectivePopupID) {
    return null;
  }

  return (
    <ModuleElementDiv className={componentProps.className ?? ''} $styleText={componentProps.styleText}>
      <DataElementContext.Provider value={contextValue}>
        {React.Children.map(componentProps.children, (child) => child)}
      </DataElementContext.Provider>
    </ModuleElementDiv>
  );
};

export default NotificationTriggeredPopup;
